import { z } from 'zod';
import { baseListQuerySchema, sortOrderSchema, } from './helpers';
import { loggedInUserMatchSchema } from './shadow-ai-logged-in-user';
import { ColumnDataTypes, createTableQuerySchema, FilterOperators, FiltersSchema, FilterValueSchema, TableResponseMetaSchema, } from './table';
import { filterOptionItemSchema } from './mcp';
import { knownTotalQuerySchema } from './known-total';
// ============================================================================
// Filterable Fields
// ============================================================================
export const RUNNING_NOW_FILTERABLE_FIELDS = {
    NAME: 'name',
    CATEGORY: 'category',
    USER: 'user',
    // KIR-3657: filter running processes by the account signed in to them.
    LOGGED_IN_USER: 'loggedInUser',
    PID: 'pid',
    PORT: 'port',
    STARTED_AT: 'startedAt',
};
// ============================================================================
// Sortable Fields
// ============================================================================
export const RUNNING_NOW_SORTABLE_FIELDS = {
    NAME: 'name',
    CATEGORY: 'category',
    USER: 'user',
    // KIR-3657: sort by the signed-in account; N/A sorts last (both directions).
    LOGGED_IN_USER: 'loggedInUser',
    PID: 'pid',
    PORT: 'port',
    STARTED_AT: 'startedAt',
};
export const runningNowSortFieldSchema = z.enum([
    RUNNING_NOW_SORTABLE_FIELDS.NAME,
    RUNNING_NOW_SORTABLE_FIELDS.CATEGORY,
    RUNNING_NOW_SORTABLE_FIELDS.USER,
    RUNNING_NOW_SORTABLE_FIELDS.LOGGED_IN_USER,
    RUNNING_NOW_SORTABLE_FIELDS.PID,
    RUNNING_NOW_SORTABLE_FIELDS.PORT,
    RUNNING_NOW_SORTABLE_FIELDS.STARTED_AT,
]);
// ============================================================================
// Searchable Fields
// ============================================================================
// startedAt intentionally omitted — DATE fields are not searchable.
export const RUNNING_NOW_SEARCHABLE_FIELDS = {
    NAME: 'name',
    CATEGORY: 'category',
    USER: 'user',
    PID: 'pid',
    PORT: 'port',
};
// ============================================================================
// Filter Option Fields (dynamic endpoints only)
// ============================================================================
// pid/port use NUMBER — user enters values directly, no dropdown endpoint.
// startedAt uses a date picker — no dynamic endpoint.
export const RUNNING_NOW_FILTER_OPTION_FIELDS = {
    NAME: 'name',
    CATEGORY: 'category',
    USER: 'user',
    LOGGED_IN_USER: 'loggedInUser',
};
export const runningNowFilterOptionFieldSchema = z.enum([
    RUNNING_NOW_FILTER_OPTION_FIELDS.NAME,
    RUNNING_NOW_FILTER_OPTION_FIELDS.CATEGORY,
    RUNNING_NOW_FILTER_OPTION_FIELDS.USER,
    RUNNING_NOW_FILTER_OPTION_FIELDS.LOGGED_IN_USER,
]);
// ============================================================================
// Response Item
// ============================================================================
export const runningNowListItemSchema = z.object({
    id: z.string().uuid(),
    toolId: z.string(),
    toolName: z.string(),
    category: z.string(),
    userId: z.string().uuid(),
    userEmail: z.string().nullable(),
    userName: z.string().nullable(),
    pid: z.number().int().positive(),
    port: z.number().int().nullable(),
    startedAt: z.string().datetime({ offset: true }),
    machineId: z.string(),
    isRoot: z.boolean(),
    binaryPath: z.string().nullable(),
    commandLine: z.string().nullable(),
    parentName: z.string().nullable(),
    extras: z.record(z.string(), z.string()).nullable(),
    // KIR-2916: the account signed in to this process + its match to the user.
    // `.default('unknown')` (not `.optional()`) for consistency with the other
    // logged-in surfaces — there is always a conceptual classification.
    loggedInUserEmail: z.string().nullish(),
    loggedInUserMatch: loggedInUserMatchSchema.default('unknown'),
});
// ============================================================================
// Filter Column Data Types (private — used internally by the query schema)
// ============================================================================
const RUNNING_NOW_FILTER_COLUMN_DATA_TYPES = {
    [RUNNING_NOW_FILTERABLE_FIELDS.NAME]: ColumnDataTypes.STRING,
    [RUNNING_NOW_FILTERABLE_FIELDS.CATEGORY]: ColumnDataTypes.ENUM,
    [RUNNING_NOW_FILTERABLE_FIELDS.USER]: ColumnDataTypes.ENUM,
    [RUNNING_NOW_FILTERABLE_FIELDS.LOGGED_IN_USER]: ColumnDataTypes.STRING,
    [RUNNING_NOW_FILTERABLE_FIELDS.PID]: ColumnDataTypes.NUMBER,
    [RUNNING_NOW_FILTERABLE_FIELDS.PORT]: ColumnDataTypes.NUMBER,
    [RUNNING_NOW_FILTERABLE_FIELDS.STARTED_AT]: ColumnDataTypes.DATE,
};
// startedAt drops equals/notEquals; pid/port keep the NUMBER defaults (no override).
export const RUNNING_NOW_OPERATOR_OVERRIDES = {
    [RUNNING_NOW_FILTERABLE_FIELDS.STARTED_AT]: [
        FilterOperators.GREATER_THAN,
        FilterOperators.LESS_THAN,
        FilterOperators.BETWEEN,
        FilterOperators.IS_EMPTY,
        FilterOperators.IS_NOT_EMPTY,
    ],
};
// ============================================================================
// Filter Options Schemas
// ============================================================================
export const runningNowFilterOptionsQuerySchema = z.object({
    field: runningNowFilterOptionFieldSchema,
    search: z.string().optional(),
    limit: z.coerce.number().int().positive().max(100).optional(),
    offset: z.coerce.number().int().min(0).optional(),
    // KIR-3495: scope the options to a single org member (Shadow AI user-scope
    // page). Honoured only for manager/owner/support — everyone else is already
    // pinned to their own rows, so this can never widen what a caller may see.
    scopeUserId: z.string().uuid().optional(),
});
export const runningNowFilterOptionsResponseSchema = z.object({
    field: runningNowFilterOptionFieldSchema,
    options: z.union([z.array(z.string()), z.array(filterOptionItemSchema)]),
    pagination: z.object({
        total: z.number().int().min(0),
        limit: z.number().int().min(0),
        offset: z.number().int().min(0),
        hasMore: z.boolean(),
        nextOffset: z.number().int().min(0).nullable().optional(),
    }),
});
const runningNowTableQueryBaseSchema = baseListQuerySchema.extend({
    sortBy: runningNowSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    filters: FiltersSchema.optional(),
    knownTotal: knownTotalQuerySchema,
    isRoot: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform(v => v === true || v === 'true')
        .optional(),
    // KIR-3495: restrict the whole result set to one org member so the Shadow AI
    // user-scope page reuses this table with per-user aggregates. Privileged
    // roles only (see the table engine) — never widens a caller's visibility.
    scopeUserId: z.string().uuid().optional(),
});
export const runningNowTableQuerySchema = createTableQuerySchema(runningNowTableQueryBaseSchema, RUNNING_NOW_FILTER_COLUMN_DATA_TYPES, RUNNING_NOW_OPERATOR_OVERRIDES)
    .superRefine((data, ctx) => {
    data.filters.forEach((filter, index) => {
        if (filter.field === RUNNING_NOW_FILTERABLE_FIELDS.USER) {
            filter.value.forEach((v, valueIndex) => {
                if (!z.string().uuid().safeParse(v).success) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Filter values for user field must be valid UUIDs',
                        path: ['filters', index, 'value', valueIndex],
                    });
                }
            });
        }
        // pid/port (NUMBER) are validated by the shared createTableQuerySchema range rules.
        if (filter.field === RUNNING_NOW_FILTERABLE_FIELDS.STARTED_AT) {
            filter.value.forEach((v, valueIndex) => {
                if (!z.string().datetime({ offset: true, local: true }).safeParse(v)
                    .success) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Filter values for startedAt field must be valid ISO 8601 datetime strings',
                        path: ['filters', index, 'value', valueIndex],
                    });
                }
            });
        }
    });
})
    .transform((data) => ({
    page: data.page,
    limit: data.limit,
    search: data.search,
    sortBy: data.sortBy,
    sortOrder: data.sortOrder,
    // Positional empties (unset range bounds) are preserved so the backend
    // can distinguish min-only / max-only pid/port filters.
    filters: data.filters,
    isRoot: data.isRoot,
    scopeUserId: data.scopeUserId,
}));
// ============================================================================
// API Client Params Schema (for serializing DataTable query params)
// ============================================================================
export const listRunningNowDataTableParamsSchema = z.object({
    page: z.coerce.number().int().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
    search: z.string().optional(),
    sortBy: runningNowSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    filters: z.array(FilterValueSchema).optional(),
    isRoot: z.boolean().optional(),
    knownTotal: knownTotalQuerySchema,
    scopeUserId: z.string().uuid().optional(),
});
// ============================================================================
// Table Response Schema
// ============================================================================
export const runningNowTableResponseSchema = z.object({
    data: z.array(runningNowListItemSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
    meta: TableResponseMetaSchema,
});
//# sourceMappingURL=shadow-ai-running-now.js.map